import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Flame } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";
import { useLiveStatus } from "../hooks/useLiveStatus";

const LiveDot = ({ on }) => (
  <span className="relative inline-flex w-2 h-2 mr-1.5 align-middle">
    {on && <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-75" />}
    <span className={`relative inline-flex w-2 h-2 rounded-full ${on ? "bg-red-500" : "bg-white/40"}`} />
  </span>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();
  const { data: live } = useLiveStatus(location.pathname);

  const navLinks = [
    { href: "/", label: t.nav.home },
    { href: "/locations", label: t.nav.explore },
    { href: "/news", label: "News" },
    ...(live?.enabled ? [{ href: "/live", label: "Live", isLive: !!live.live }] : []),
    { href: "/tools", label: "Tools" },
    { href: "/about", label: t.nav.about },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location]);

  // Glassmorphism style used across nav + mobile bar
  const glassStyle = {
    background: scrolled
      ? "rgba(28,28,30,0.72)"
      : "rgba(28,28,30,0.5)",
    backdropFilter: "blur(24px) saturate(160%)",
    WebkitBackdropFilter: "blur(24px) saturate(160%)",
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow:
      "0 10px 40px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06)",
    transition: "background 0.3s ease, box-shadow 0.3s ease",
  };

  return (
    <>
      {/* Desktop Floating Glass Nav — visible from lg (≥1024px) */}
      <div
        className="fixed top-4 left-1/2 z-50 hidden lg:block"
        style={{ transform: "translateX(-50%)" }}
      >
        <motion.nav
          initial={{ y: -80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="flex items-center gap-1 px-4 py-2 rounded-full"
          style={{ ...glassStyle, maxWidth: "min(95vw, 1100px)" }}
          data-testid="main-navbar"
        >
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 mr-3 group flex-shrink-0"
            data-testid="nav-logo"
          >
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-saffron to-orange-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <Flame size={14} className="text-white" />
            </div>
            <span className="font-heading font-bold text-sm text-white whitespace-nowrap">
              Ujjain Maha Kumbh
            </span>
          </Link>

          {/* Links */}
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className={`px-3 py-1.5 rounded-full text-sm font-body font-medium whitespace-nowrap transition-all duration-200 ${
                location.pathname === link.href
                  ? "bg-saffron/20 text-saffron"
                  : "text-white/75 hover:text-white hover:bg-white/[0.06]"
              }`}
            >
              {link.href === "/live" && <LiveDot on={link.isLive} />}
              {link.label}
            </Link>
          ))}

          {/* Register CTA */}
          <Link
            to="/register"
            data-testid="nav-register-btn"
            className="ml-1.5 px-4 py-1.5 rounded-full text-sm font-body font-semibold bg-saffron text-stone-900 hover:bg-saffron-light transition-all duration-200 hover:shadow-[0_0_15px_rgba(255,153,51,0.5)] active:scale-95 whitespace-nowrap flex-shrink-0"
          >
            {t.nav.register}
          </Link>

          {/* Language Switcher */}
          <div className="ml-1.5 flex-shrink-0">
            <LanguageSwitcher />
          </div>
        </motion.nav>
      </div>

      {/* Mobile / Tablet Nav — visible <lg (≤1023px) */}
      <div
        className="fixed top-3 left-3 right-3 z-50 flex lg:hidden items-center justify-between px-4 py-3 rounded-2xl"
        style={glassStyle}
        data-testid="mobile-navbar"
      >
        <Link to="/" className="flex items-center gap-2 min-w-0" data-testid="nav-logo-mobile">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-saffron to-orange-600 flex items-center justify-center flex-shrink-0">
            <Flame size={14} className="text-white" />
          </div>
          <span className="font-heading font-bold text-sm text-white truncate">
            Ujjain Maha Kumbh
          </span>
        </Link>
        <div className="flex items-center gap-2 flex-shrink-0">
          <LanguageSwitcher />
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="text-white/80 hover:text-white p-1"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.2 }}
            className="fixed top-[68px] left-3 right-3 z-40 rounded-2xl p-3 flex flex-col gap-1 lg:hidden"
            style={{
              background: "rgba(28,28,30,0.9)",
              border: "1px solid rgba(255,255,255,0.08)",
              backdropFilter: "blur(24px) saturate(160%)",
              WebkitBackdropFilter: "blur(24px) saturate(160%)",
              boxShadow:
                "0 20px 60px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)",
            }}
            data-testid="mobile-menu-drawer"
          >
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  location.pathname === link.href
                    ? "bg-saffron/20 text-saffron"
                    : "text-white/75 hover:text-white hover:bg-white/5"
                }`}
              >
                {link.href === "/live" && <LiveDot on={link.isLive} />}
                {link.label}
              </Link>
            ))}
            <Link
              to="/register"
              className="mt-1 px-4 py-3 rounded-xl text-sm font-semibold bg-saffron text-stone-900 text-center"
              data-testid="mobile-register-btn"
            >
              {t.nav.register}
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
